import Vue from 'vue'
import Router from 'vue-router'
import VueCookie from 'vue-cookie'
import store from '@/store/index'
import VueCryptojs from 'vue-cryptojs'
import orders from './modules/order'
import ccsheet from './modules/ccsheet'

// Install plugins
Vue.use(VueCryptojs)
Vue.use(Router)

// Suppress unhandled promise rejections from expected navigation failures
// (NavigationDuplicated, Redirected, Cancelled, Aborted) while still
// propagating real errors.
const originalPush = Router.prototype.push
Router.prototype.push = function push (location, onResolve, onReject) {
  if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject)
  return originalPush.call(this, location).catch(err => {
    if (err && err._isRouter) return
    throw err
  })
}

/**
 * Helper to decrypt a string using the same key you used previously.
 * (We need this later for permissions, if you keep that logic.)
 */
const decryptString = (cipherText) => {
  try {
    const bytes = Vue.CryptoJS.AES.decrypt(cipherText, '2020ModulesPermissionKey@TimerApp')
    return bytes.toString(Vue.CryptoJS.enc.Utf8)
  } catch (e) {
    return null
  }
}

/**
 * Check a permission‐cookie to see if the current user can access a given module.
 * This is used in router.beforeEach to prevent unauthorized access.
 */
const checkListPermission = (moduleName) => {
  const blob = VueCookie.get(process.env.VUE_APP_PERMISSIONS_AND_ROLES)
  if (!blob) return false
  const decrypted = decryptString(blob)
  if (!decrypted) return false
  const { permissions, role } = JSON.parse(decrypted)
  // Superusers always allowed:
  if (['admin', 'superadmin'].includes(role?.slug)) return true
  return (permissions[moduleName] && permissions[moduleName].list) || false
}

/**
 * Weld Mapping access, read from the same permission cookie so a direct URL hit
 * is checked before the store is hydrated. The rule itself is the web's — the
 * backend folds the org feature flag into `weld-mapping.list` (see
 * CommonAPIController) and this only reads the verdict.
 *
 * Fail closed: no cookie / undecryptable / missing key → no access. Admins are
 * deliberately not exempt, since the org feature flag gates the whole tenant.
 */
// Exported (5183 / R-2.7d) so a view can ask the same question the guard asks,
// instead of carrying a second copy of the rule that would drift from it.
export const checkWeldMappingPermission = () => {
  const blob = VueCookie.get(process.env.VUE_APP_PERMISSIONS_AND_ROLES)
  if (!blob) return false
  const decrypted = decryptString(blob)
  if (!decrypted) return false
  try {
    const { permissions } = JSON.parse(decrypted)
    return Boolean(permissions && permissions['weld-mapping'] && permissions['weld-mapping'].list)
  } catch (e) {
    return false
  }
}

// Build a flat array of routes. All should be registered up front.
const routes = [
  // 1) Catch‐all redirect (last fallback)
  { path: '*', redirect: '/dashboard' },

  // 2) Authentication pages
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/Auth/Login.vue'),
    beforeEnter (to, from, next) {
      if (!VueCookie.get(process.env.VUE_APP_TOKEN)) {
        next()
      } else {
        next('/dashboard')
      }
    }
  },
  {
    path: '/directlogin/:otp/:domain/:lang/:user_id',
    name: 'directlogin',
    component: () => import('@/views/Auth/DirectLogin.vue')
  },

  // 3) Dashboard (protected)
  {
    path: '/dashboard',
    component: () => import('@/views/Dashboard/Index.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/Dashboard/List.vue')
      }
    ]
  },

  // 4) Other top‐level routes (protected)
  {
    path: '/myshifts',
    name: 'Shifts',
    meta: { requiresAuth: true },
    component: () => import('@/views/MyShifts/MyShift.vue')
  },
  {
    path: '/news',
    name: 'News',
    meta: { requiresAuth: true },
    component: () => import('@/views/News/Index.vue')
  },
  {
    path: '/not_found',
    name: 'NotFound',
    component: () => import('@/components/NotFound.vue')
  },

  // 5) /projects and all nested children
  {
    path: '/projects',
    component: () => import('@/views/Projects/Index.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'projects',
        meta: { moduleName: 'projects' },
        component: () => import('@/views/Projects/List.vue')
      },
      {
        path: ':project_id',
        meta: { moduleName: 'projects' },
        component: () => import('@/views/Projects/ProjectModuleView.vue'),
        beforeEnter: async (to, from, next) => {
          try {
            await store.dispatch('project/getProject', to.params.project_id)
            next()
          } catch (e) {
            console.error('Error fetching project:', e)
            next('/projects')
          }
        },
        children: [
          {
            path: '',
            name: 'project_action',
            component: () => import('@/views/Projects/Actions.vue')
          },
          {
            // 4975 — internal project chat. moduleName is 'projects' on
            // purpose: there is no separate chat permission, whoever may open
            // the project may use its chat, and WHO may read WHAT is decided
            // server-side by ProjectChatTrait's two-stage gate.
            path: 'chat',
            name: 'project_chat',
            meta: { moduleName: 'projects' },
            component: () => import('@/views/Projects/Chat.vue')
          },
          {
            path: 'custom_modules/:module_id',
            component: () => import('@/views/Projects/CustomModules/Index.vue'),
            children: [
              {
                path: '',
                name: 'project_custom_modules',
                meta: { moduleName: 'custommodule' },
                component: () => import('@/views/Projects/CustomModules/List.vue'),
                props: true
              },
              {
                path: 'action/:record_id?',
                name: 'project_custom_module_action',
                meta: { moduleName: 'custommodule' },
                component: () => import('@/views/Projects/CustomModules/Actions.vue')
              }
            ]
          },
          {
            path: 'module_report',
            name: 'project_module_report',
            component: () => import('@/views/ModuleReport/Index.vue')
          },
          {
            path: 'drawing',
            component: () => import('@/views/Projects/Drawings/Index.vue'),
            meta: { requiresAuth: true, moduleName: 'projectdrawing' },
            children: [
              {
                path: '',
                name: 'drawing',
                meta: { moduleName: 'projectdrawing' },
                component: () => import('@/views/Projects/Drawings/List.vue')
              },
              {
                path: 'action/:id?',
                name: 'drawing_actions',
                meta: { moduleName: 'projectdrawing' },
                component: () => import('@/views/Projects/Drawings/Actions.vue')
              }
            ]
          },
          {
            path: 'ndt',
            component: () => import('@/views/Projects/NDT/Index.vue'),
            meta: { requiresAuth: true, moduleName: 'projectndt' },
            children: [
              {
                path: '',
                name: 'ndt',
                meta: { moduleName: 'projectndt' },
                component: () => import('@/views/Projects/NDT/List.vue')
              },
              {
                path: 'action/:id?',
                name: 'ndt_actions',
                meta: { moduleName: 'projectndt' },
                component: () => import('@/views/Projects/NDT/Actions.vue')
              }
            ]
          },
          {
            path: 'pressure-test-report',
            component: () => import('@/views/Projects/PressureTestReport/Index.vue'),
            meta: { requiresAuth: true, moduleName: 'projectpressuretest' },
            children: [
              {
                path: '',
                name: 'pressure-test-report',
                meta: { moduleName: 'projectpressuretest' },
                component: () => import('@/views/Projects/PressureTestReport/List.vue')
              },
              {
                path: 'action/:id?',
                name: 'pressure-test-report_actions',
                meta: { moduleName: 'projectpressuretest' },
                component: () => import('@/views/Projects/PressureTestReport/Actions.vue')
              }
            ]
          },
          {
            path: 'weld-log',
            component: () => import('@/views/Projects/WeldLog/Index.vue'),
            meta: { requiresAuth: true, moduleName: 'projectweldlog' },
            children: [
              {
                path: '',
                name: 'weld-log',
                meta: { moduleName: 'projectweldlog' },
                component: () => import('@/views/Projects/WeldLog/List.vue')
              },
              {
                path: 'action/:id?',
                name: 'weld-log_actions',
                meta: { moduleName: 'projectweldlog' },
                component: () => import('@/views/Projects/WeldLog/Actions.vue')
              }
            ]
          },
          {
            // Weld Mapping drawing-selection entry page. Declared BEFORE the
            // `advanced-weld-log/:drawing_id?` route below: routes are matched in
            // order and that optional param would otherwise swallow
            // "weld-mapping" as a drawing_id, making this page unreachable.
            // Guarded here so a direct URL is protected, not just a hidden link.
            path: 'advanced-weld-log/weld-mapping',
            component: () => import('@/views/Projects/AdvancedWeldLog/Index.vue'),
            meta: { requiresAuth: true, moduleName: 'advancedweldlog' },
            children: [{
              path: '',
              name: 'advanced-weld-log_weld-mapping',
              meta: { moduleName: 'advancedweldlog' },
              component: () => import('@/views/Projects/AdvancedWeldLog/WeldMappingDrawings.vue')
            }, {
              // Single drawing screen. Sits under the same guarded parent, so
              // the weld-mapping permission check covers it too.
              path: ':drawing_id',
              name: 'weld-mapping-drawing',
              meta: { moduleName: 'advancedweldlog' },
              component: () => import('@/views/Projects/AdvancedWeldLog/WeldMappingDrawing.vue')
            }],
            beforeEnter (to, from, next) {
              if (checkWeldMappingPermission()) next()
              // No permission → fall back to the standard advanced weld log
              // rather than a dead end.
              else next({ name: 'advanced-weld-log', params: { project_id: to.params.project_id } })
            }
          },
          {
            path: 'advanced-weld-log/:drawing_id?',
            component: () => import('@/views/Projects/AdvancedWeldLog/Index.vue'),
            meta: { requiresAuth: true, moduleName: 'advancedweldlog' },
            children: [{
              // Must be '' (relative), not '/'. An absolute child path resolves
              // from the root, so this child used to register as `/` and never
              // matched `/projects/:project_id/advanced-weld-log` — the parent
              // shell rendered an empty <router-view>, i.e. a blank page.
              path: '',
              name: 'advanced-weld-log',
              meta: { moduleName: 'advancedweldlog' },
              component: () => import('@/views/Projects/AdvancedWeldLog/ProjectWeldLog.vue')
            }]
          },
          {
            path: 'surface-report',
            component: () => import('@/views/Projects/SurfaceReport/Index.vue'),
            meta: { requiresAuth: true, moduleName: 'projectsurfacereport' },
            children: [
              {
                path: '',
                name: 'surface-report',
                meta: { moduleName: 'projectsurfacereport' },
                component: () => import('@/views/Projects/SurfaceReport/List.vue')
              },
              {
                path: 'action/:id?',
                name: 'surface-report_actions',
                meta: { moduleName: 'projectsurfacereport' },
                component: () => import('@/views/Projects/SurfaceReport/Actions.vue')
              }
            ]
          },
          {
            path: 'technical-report',
            component: () => import('@/views/Projects/TechnicalReport/Index.vue'),
            meta: { requiresAuth: true, moduleName: 'projecttechnicalreport' },
            children: [
              {
                path: '',
                name: 'technical-report',
                meta: { moduleName: 'projecttechnicalreport' },
                component: () => import('@/views/Projects/TechnicalReport/List.vue')
              },
              {
                path: 'action/:id?',
                name: 'technical-report_actions',
                meta: { moduleName: 'projecttechnicalreport' },
                component: () => import('@/views/Projects/TechnicalReport/Actions.vue')
              }
            ]
          },
          {
            path: 'material-list',
            component: () => import('@/views/Projects/MatrialList/Index.vue'),
            meta: { requiresAuth: true, moduleName: 'projectmateriallist' },
            children: [
              {
                path: '',
                name: 'material-list',
                meta: { moduleName: 'projectmateriallist' },
                component: () => import('@/views/Projects/MatrialList/List.vue')
              },
              {
                path: 'action/:id?',
                name: 'material-list_actions',
                meta: { moduleName: 'projectmateriallist' },
                component: () => import('@/views/Projects/MatrialList/Actions.vue')
              }
            ]
          },
          {
            path: 'pwht',
            component: () => import('@/views/Projects/PWHT/Index.vue'),
            meta: { requiresAuth: true, moduleName: 'projectpwht' },
            children: [
              {
                path: '',
                name: 'pwht',
                meta: { moduleName: 'projectpwht' },
                component: () => import('@/views/Projects/PWHT/List.vue')
              },
              {
                path: 'action/:id?',
                name: 'pwht_actions',
                meta: { moduleName: 'projectpwht' },
                component: () => import('@/views/Projects/PWHT/Actions.vue')
              }
            ]
          },
          {
            path: 'pwps',
            component: () => import('@/views/Projects/PWPS/Index.vue'),
            meta: { requiresAuth: true, moduleName: 'projectpwps' },
            children: [
              {
                path: '',
                name: 'pwps',
                meta: { moduleName: 'projectpwps' },
                component: () => import('@/views/Projects/PWPS/List.vue')
              }
            ]
          },
          {
            path: 'wps',
            component: () => import('@/views/Projects/WPS/Index.vue'),
            meta: { requiresAuth: true, moduleName: 'projectwps' },
            children: [
              {
                path: '',
                name: 'wps',
                meta: { moduleName: 'projectwps' },
                component: () => import('@/views/Projects/WPS/List.vue')
              }
            ]
          }
        ]
      }
    ]
  },

  // 6) /projects/:project_id/materials
  {
    path: '/projects/:project_id/materials',
    component: () => import('@/views/Warehouse/orders/customer/View'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        component: () => import('@/views/Warehouse/orders/customer/Item')
      },
      {
        path: 'pick/:product_code?',
        name: 'project_pick_material',
        component: () => import('@/views/Warehouse/orders/customer/Pick')
      }
    ]
  },

  // 7) /projects/:project_id/checklists
  {
    path: '/projects/:project_id/checklists/:checklist_id?',
    name: 'project_checklist',
    component: () => import('@/views/Projects/Checklist/List.vue')
  },

  // 8) /projects/:project_id/equipments
  {
    path: '/projects/:project_id/equipments',
    name: 'project_equipment',
    meta: { moduleName: 'projectequipment' },
    component: () => import('@/views/Equipments/NewListEquipement.vue')
  },

  // 9) /projects/:project_id/deviations
  {
    path: '/projects/:project_id/deviations',
    name: 'project_deviation',
    meta: { moduleName: 'projectdeviation' },
    component: () => import('@/views/Deviations/NewList.vue')
  },

  // 10) /files
  {
    path: '/files',
    component: () => import('@/views/Files/Index.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'files',
        component: () => import('@/views/Files/Files.vue')
      }
    ]
  },

  // 11) /deviations
  {
    path: '/deviations',
    component: () => import('@/views/Deviations/Index.vue'),
    meta: { requiresAuth: true, moduleName: 'deviation' },
    children: [
      {
        path: '',
        name: 'deviations',
        meta: { moduleName: 'deviation' },
        component: () => import('@/views/Deviations/NewList.vue')
      },
      {
        path: 'add',
        name: 'deviation_add',
        meta: { moduleName: 'deviation' },
        component: () => import('@/views/Deviations/Actions.vue')
      },
      {
        path: ':id',
        name: 'deviation_edit',
        meta: { moduleName: 'deviation' },
        component: () => import('@/views/Deviations/Actions.vue')
      }
    ]
  },

  // 12) /calendar
  {
    path: '/calendar',
    component: () => import('@/views/Calendar/Index.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'calendar',
        component: () => import('@/views/Calendar/Calendar.vue')
      },
      {
        path: 'actions/:hour_id?',
        name: 'calendar_actions',
        component: () => import('@/views/Calendar/Action.vue')
      }
    ]
  },

  // 12b) /flex-account (fleksitid)
  {
    path: '/flex-account',
    component: () => import('@/views/FlexAccount/Index.vue'),
    meta: { requiresAuth: true, requiresFlex: true },
    children: [
      {
        path: '',
        name: 'flex-account',
        component: () => import('@/views/FlexAccount/Balance.vue')
      },
      {
        path: 'transactions',
        name: 'flex-transactions',
        component: () => import('@/views/FlexAccount/Transactions.vue')
      },
      {
        path: 'avspasering',
        name: 'flex-avspasering',
        component: () => import('@/views/FlexAccount/Avspasering.vue')
      },
      {
        path: 'egenmelding',
        name: 'flex-egenmelding',
        component: () => import('@/views/FlexAccount/Egenmelding.vue')
      }
    ]
  },

  // 12c) /overtime (per-user overtime overview)
  {
    path: '/overtime',
    name: 'overtime',
    meta: { requiresAuth: true, requiresFlex: true },
    component: () => import('@/views/Overtime/Index.vue')
  },

  // 13) /stemp
  {
    path: '/stemp',
    name: 'stemp',
    meta: { requiresAuth: true },
    component: require('@/views/Timer/Stemp.vue').default
  },

  // 14) /global_checklist
  {
    path: '/global_checklist',
    component: () => import('@/views/GlobalChecklist/Index.vue'),
    meta: { requiresAuth: true, moduleName: 'checklists' },
    children: [
      {
        path: '',
        name: 'global_checklist',
        meta: { moduleName: 'checklists' },
        component: () => import('@/views/GlobalChecklist/List.vue')
      }
    ]
  },

  // 15) /chemicallist
  {
    path: '/chemicallist',
    component: () => import('@/views/Chemicallist/Index.vue'),
    meta: { requiresAuth: true, moduleName: 'chemicallist' },
    children: [
      {
        path: '',
        name: 'chemicallist',
        meta: { moduleName: 'chemicallist' },
        component: () => import('@/views/Chemicallist/NewList.vue')
      },
      {
        path: ':chemicallist_id/checklists',
        name: 'chemicallist_checklist',
        meta: { moduleName: 'chemicallist' },
        component: () => import('@/views/Chemicallist/Checklist/List.vue')
      },
      {
        path: ':id',
        name: 'chemicallist_edit',
        meta: { moduleName: 'chemicallist' },
        component: () => import('@/views/Chemicallist/Actions.vue')
      }
    ]
  },

  // 16) /equipments
  {
    path: '/equipments',
    component: () => import('@/views/Equipments/Index.vue'),
    meta: { requiresAuth: true, moduleName: 'equipments' },
    children: [
      {
        path: '',
        name: 'equipments',
        meta: { moduleName: 'equipments' },
        component: () => import('@/views/Equipments/NewListEquipement.vue')
      },
      {
        path: 'add',
        name: 'equipment_add',
        meta: { moduleName: 'equipments' },
        component: () => import('@/views/Equipments/Actions.vue')
      },
      {
        path: ':id',
        name: 'equipment_edit',
        meta: { moduleName: 'equipments' },
        component: () => import('@/views/Equipments/Actions.vue')
      }
    ]
  },

  // 17) /equipments/:equipment_id/checklists
  {
    path: '/equipments/:equipment_id/checklists',
    name: 'equipment_checklist',
    component: () => import('@/views/Equipments/Checklist/List.vue')
  },

  // 18) /warehouse
  {
    path: '/warehouse',
    component: () => import('@/views/Warehouse/dashboard/Index'),
    meta: { requiresAuth: true, moduleName: 'warehouse' }
  },
  // 19) Spread in routes from orders and ccsheet modules
  ...orders,
  ...ccsheet,

  // 20) /tasks
  {
    path: '/tasks',
    meta: { requiresAuth: true },
    component: () => import('@/views/Tasks/Index.vue'),
    children: [
      {
        path: '',
        name: 'Tasks',
        component: () => import('@/views/Tasks/List.vue')
      }
    ]
  },
  // 21) /checklist_files
  {
    path: '/checklist_files',
    component: () => import('@/views/ChecklistFiles/ChecklistFiles.vue'),
    meta: { requiresAuth: true }
  }
]

const router = new Router({
  mode: 'history',
  routes
})

// Suppress NavigationDuplicated / Redirected / Cancelled / Aborted errors that
// originate from guard-triggered redirects (next('/path')) — these are expected
// and not caught by the Router.prototype.push patch above.
router.onError((err) => {
  if (err && err._isRouter) return
  // A chunk of an older build that is no longer on the server (after a
  // deploy). Reload once into the new version. The flag stops a reload loop
  // when the chunk is missing for another reason; it is cleared again after
  // the next successful navigation (afterEach below), so a LATER deploy in the
  // same session can recover too — before, one reload per session was all a
  // user ever got, and the next deploy left them stuck.
  if (err && (err.name === 'ChunkLoadError' || /loading (css )?chunk/i.test(err.message))) {
    if (!sessionStorage.getItem('chunk_reload_attempted')) {
      sessionStorage.setItem('chunk_reload_attempted', '1')
      window.location.reload()
      return
    }
  }
  throw err
})
router.afterEach(() => {
  try { sessionStorage.removeItem('chunk_reload_attempted') } catch (e) { /* storage unavailable */ }
})

// Modules that require a permission check
const permissionModules = [
  'custommodule',
  'equipments',
  'chemicallist',
  'deviation',
  'checklists',
  'projects',
  'projectdeviation',
  'projectequipment',
  'projectdrawing',
  'projectndt',
  'projectpressuretest',
  'projectweldlog',
  'projectsurfacereport',
  'projecttechnicalreport',
  'projectmateriallist',
  'projectpwht',
  'projectpwps',
  'projectwps',
  'warehouse'
]

// Global navigation guard
router.beforeEach((to, from, next) => {
  // Check if route requires auth
  if (to.matched.some(record => record.meta.requiresAuth)) {
    const token = VueCookie.get(process.env.VUE_APP_TOKEN)

    // No token → redirect to login
    if (!token) {
      store.commit('resetAuth', false)
      store.commit('resetPermissions', false)
      VueCookie.delete(process.env.VUE_APP_TOKEN)

      return next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
    }

    // Get moduleName safely (deepest matched route with moduleName)
    const moduleRoute = to.matched
      .slice()
      .reverse()
      .find(r => r.meta?.moduleName)

    const moduleName = moduleRoute?.meta?.moduleName

    // Get features from localStorage
    const features = JSON.parse(localStorage.getItem('features') || '{}')

    // Helper to get feature (supports custom_modules also)
    const getFeature = (features, moduleName) => {
      if (features[moduleName]) return features[moduleName]

      if (features.custom_modules && features.custom_modules[moduleName]) {
        return features.custom_modules[moduleName]
      }

      return null
    }

    if (moduleName && permissionModules.includes(moduleName)) {
      const feature = getFeature(features, moduleName)

      if (feature && feature.enabled === 0) {
        return next('/dashboard') // or '/not_found'
      }

      // Permission denied → block access
      if (moduleName === 'checklists' && !checkListPermission(moduleName)) {
        return next('/dashboard')
      }
    }
    return next()
  }
  next()
})

// 4993: flex-only screens (requiresFlex). The flag comes from the timer API
// (/settings/app → generalSettings.flex_hours). On a direct URL hit the store
// may still be empty, so load the settings once before deciding; anything that
// fails or stays false bounces to the calendar (fail closed).
router.beforeEach(async (to, from, next) => {
  if (!to.matched.some(record => record.meta && record.meta.requiresFlex)) return next()
  if (store.getters.flexHoursEnabled) return next()
  try {
    const settings = store.state.common.generalSettings
    if (!settings || settings.flex_hours === undefined) {
      await store.dispatch('getGeneralSettings')
    }
  } catch (e) {
    // fall through: fail closed
  }
  return store.getters.flexHoursEnabled ? next() : next({ path: '/calendar' })
})

export default router
